import React, { useState, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  Printer,
  Check,
  Star,
  TrendingUp,
  Globe,
  BarChart,
  Users,
  Calendar,
  Clock,
  Key,
  Search,
  Mail,
  Server,
  Zap,
  AlertCircle
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const PremiumPackPresentation: React.FC = () => {
  const [clientName, setClientName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const documentRef = useRef<HTMLDivElement>(null);

  const handleDownloadPDF = async () => {
    if (!documentRef.current || isGenerating) return;
    
    setIsGenerating(true);
    
    try {
      const element = documentRef.current;
      
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
        hotfixes: ['px_scaling']
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 0;
      const totalPages = Math.ceil(imgHeight / pdfHeight);

      while (heightLeft > 0) {
        if (pageNumber > 0) {
          pdf.addPage();
        }
        
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0,
          position,
          imgWidth,
          imgHeight,
          undefined,
          'FAST'
        );
        
        // Ajouter numéro de page
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Page ${pageNumber + 1} / ${totalPages}`,
          pdfWidth / 2,
          pdfHeight - 20,
          { align: 'center' }
        );
        
        heightLeft -= pdfHeight;
        position -= pdfHeight;
        pageNumber++;
      }

      const fileName = clientName 
        ? `Pack_Premium_STRATOS_${clientName.replace(/\s+/g, '_')}.pdf`
        : 'Pack_Premium_STRATOS.pdf';
      
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setIsGenerating(false);
    }
  };

  const premiumFeatures = [
    {
      icon: Search,
      title: 'Audit SEO Technique Complet',
      description: 'Crawl approfondi, analyse vitesse, mobile-first, structure optimale',
      details: 'Identification de tous les points bloquants et opportunités d\'optimisation'
    },
    {
      icon: TrendingUp,
      title: 'Recherche de Mots-Clés Approfondie',
      description: 'Minimum 100 mots-clés analysés et priorisés',
      details: 'Clustering sémantique, analyse de l\'intention, volumétrie et difficulté'
    },
    {
      icon: Globe,
      title: 'Optimisation Internationale',
      description: 'Couverture 3 langues (FR + 2 au choix)',
      details: 'Hreflang, contenus multilingues, stratégie par marché'
    },
    {
      icon: BarChart,
      title: 'Stratégie de Contenu Avancée',
      description: 'Calendrier éditorial et plan de contenu personnalisé',
      details: 'Briefs SEO, recommandations éditoriales, optimisation sémantique'
    },
    {
      icon: Users,
      title: 'Consulting Stratégique',
      description: '2 heures par mois en visioconférence',
      details: 'Points d\'avancement, recommandations personnalisées, formation'
    },
    {
      icon: Calendar,
      title: 'Réunions Régulières',
      description: 'Bi-mensuelles les 2 premiers mois, puis mensuelles',
      details: 'Suivi rapproché en phase de lancement pour un démarrage optimal'
    },
    {
      icon: Star,
      title: 'Google Business Profile Pro',
      description: 'Création, optimisation et gestion complète',
      details: 'Posts réguliers, gestion avis, photos optimisées, local pack'
    },
    {
      icon: Zap,
      title: 'Plateforme STRATOS',
      description: 'Accès à votre dashboard personnalisé',
      details: 'Suivi en temps réel des KPIs, reporting automatisé, insights'
    },
    {
      icon: Clock,
      title: 'Support Prioritaire',
      description: 'Réponse sous 24h maximum',
      details: 'Email, plateforme STRATOS, visio sur rendez-vous'
    },
    {
      icon: TrendingUp,
      title: 'Veille Concurrentielle',
      description: 'Analyse mensuelle de vos principaux concurrents',
      details: 'Positions, stratégies, opportunités de différenciation'
    }
  ];

  const requiredAccess = [
    {
      icon: Globe,
      service: 'WordPress / CMS Admin',
      priority: 'essential',
      instructions: 'Créer un compte administrateur : stratos@stratos360.fr',
      why: 'Pour optimiser les balises, contenus, structure et vitesse du site'
    },
    {
      icon: BarChart,
      service: 'Google Analytics 4',
      priority: 'essential',
      instructions: 'Ajouter comme "Éditeur" dans Google Analytics',
      why: 'Suivi du trafic, conversions et comportement utilisateurs'
    },
    {
      icon: Search,
      service: 'Google Search Console',
      priority: 'essential',
      instructions: 'Ajouter comme "Utilisateur complet" dans GSC',
      why: 'Monitoring positions, indexation, performances SEO'
    },
    {
      icon: Star,
      service: 'Google Business Profile',
      priority: 'essential',
      instructions: 'Ajouter comme "Gestionnaire" dans GBP',
      why: 'Optimisation et gestion de votre fiche locale Google'
    },
    {
      icon: Mail,
      service: 'Email Professionnel',
      priority: 'essential',
      instructions: 'Fournir email principal pour communications',
      why: 'Rapports mensuels et communications importantes'
    },
    {
      icon: BarChart,
      service: 'Google Tag Manager',
      priority: 'recommended',
      instructions: 'Accès utilisateur avec droits de publication (si utilisé)',
      why: 'Gestion des balises de tracking et événements'
    },
    {
      icon: Server,
      service: 'Accès FTP/SFTP',
      priority: 'recommended',
      instructions: 'Fournir identifiants si interventions techniques nécessaires',
      why: 'Optimisations techniques avancées si besoin'
    },
    {
      icon: Users,
      service: 'Facebook Business Manager',
      priority: 'optional',
      instructions: 'Ajouter comme admin de la page (si applicable)',
      why: 'Synchronisation avec GBP et stratégie social media'
    }
  ];

  const deliverables = [
    { week: 'Semaine 1', item: 'Réunion kick-off + Audit initial' },
    { week: 'Semaine 2', item: 'Rapport d\'audit complet + Roadmap' },
    { week: 'Semaine 3', item: 'Recherche mots-clés + Quick wins' },
    { week: 'Semaine 4', item: 'Optimisations techniques + Contenu' },
    { week: 'Mois 2-3', item: 'Stratégie internationale + GBP' },
    { week: 'Récurrent', item: 'Reporting mensuel + Consulting' }
  ];

  return (
    <div className="space-y-6">
      {/* Form Header */}
      <Card className="p-6 print:hidden">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Présentation Pack Premium
        </h2>
        <p className="text-gray-600 mb-6">
          Document de présentation élégant du Pack Premium avec détails des prestations et besoins d'accès.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <input
            type="text"
            placeholder="Nom de l'établissement (optionnel)"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
          />
        </div>

        <div className="flex gap-3">
          <Button onClick={() => window.print()}>
            <Printer className="w-4 h-4 mr-2" />
            Imprimer
          </Button>
          <Button 
            variant="outline" 
            onClick={handleDownloadPDF}
            disabled={isGenerating}
          >
            <Download className="w-4 h-4 mr-2" />
            {isGenerating ? 'Génération...' : 'Télécharger PDF'}
          </Button>
          <Button variant="outline">
            <Mail className="w-4 h-4 mr-2" />
            Envoyer au client
          </Button>
        </div>
        {isGenerating && (
          <p className="text-sm text-blue-600 mt-3 animate-pulse">
            ⏳ Génération du PDF en cours... Veuillez patienter.
          </p>
        )}
      </Card>

      {/* Document */}
      <div ref={documentRef}>
      <Card className="bg-white print:shadow-none print:border-0">
        {/* Cover Page */}
        <div className="relative h-screen flex flex-col items-center justify-center p-12 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white print:break-after-page">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-stratos-gold blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full bg-stratos-gold blur-3xl"></div>
          </div>
          
          <div className="relative z-10 text-center space-y-8">
            <div className="mb-12">
              <h1 
                className="text-7xl font-bold mb-4"
                style={{
                  background: 'linear-gradient(to right, #E9BC66, #F4D89E)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                STRATOS
              </h1>
              <p className="text-3xl text-gray-300 font-light">360</p>
            </div>

            <div className="my-16">
              <Badge 
                variant="default" 
                className="text-2xl px-8 py-4 mb-6"
                style={{ backgroundColor: '#E9BC66', color: '#1A1A1A' }}
              >
                PACK PREMIUM
              </Badge>
              <p className="text-2xl text-gray-200 font-light mt-6">
                Solution SEO & GEO Complète
              </p>
              <p className="text-xl text-gray-400 mt-2">
                avec Consulting Stratégique
              </p>
            </div>

            {clientName && (
              <div className="mt-16 pt-8 border-t border-gray-700">
                <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">
                  Proposition pour
                </p>
                <p className="text-3xl font-bold text-stratos-gold">
                  {clientName}
                </p>
              </div>
            )}

            <div className="mt-20 text-gray-500 text-sm">
              <p>{new Date().toLocaleDateString('fr-FR', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</p>
            </div>
          </div>
        </div>

        {/* Page 2: Extrait Audit Confidentiel */}
        <div className="p-12 print:break-after-page">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-red-600" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  EXTRAIT CONFIDENTIEL – AUDIT STRATOS
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Crédibilisation et diagnostic d'opportunité
                </p>
              </div>
            </div>

            {/* Introduction */}
            <div className="mb-8 p-6 bg-blue-50 border-l-4 border-blue-600 print:break-inside-avoid">
              <p className="text-sm text-gray-700 leading-relaxed">
                L'audit STRATOS Hospitality met en lumière un <strong>potentiel de croissance important</strong>. 
                Votre établissement bénéficie d'une identité forte et d'une réputation solide, mais certains 
                aspects de la stratégie digitale peuvent encore être optimisés pour refléter pleinement la 
                qualité de l'expérience vécue sur place.
              </p>
            </div>

            {/* Tableau de synthèse */}
            <div className="mb-8 print:break-inside-avoid">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Tableau de synthèse – Diagnostic
              </h3>
              
              <div className="overflow-hidden rounded-lg border-2 border-gray-200 print:break-inside-avoid">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="text-left p-4 font-semibold">Domaine évalué</th>
                      <th className="text-left p-4 font-semibold">Observation principale</th>
                      <th className="text-center p-4 font-semibold w-24">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-red-50">
                      <td className="p-4 font-semibold text-gray-900">
                        Visibilité internationale
                      </td>
                      <td className="p-4 text-gray-700">
                        <strong>Potentiel inexploité :</strong> 7% seulement du trafic provient de l'international. 
                        Absence totale sur requêtes EN/ES/PT à fort volume.
                      </td>
                      <td className="p-4 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
                          <span className="text-lg font-bold text-red-700">1.5</span>
                        </div>
                        <p className="text-xs text-red-600 mt-1">/10</p>
                      </td>
                    </tr>
                    <tr className="border-b bg-yellow-50">
                      <td className="p-4 font-semibold text-gray-900">
                        Google Business Profile
                      </td>
                      <td className="p-4 text-gray-700">
                        Fiche active (4.6★, 2847 avis) mais <strong>75% de complétude</strong>. Posts irréguliers, 
                        potentiel +150% visibilité locale.
                      </td>
                      <td className="p-4 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-100">
                          <span className="text-lg font-bold text-yellow-700">4.5</span>
                        </div>
                        <p className="text-xs text-yellow-600 mt-1">/10</p>
                      </td>
                    </tr>
                    <tr className="border-b bg-red-50">
                      <td className="p-4 font-semibold text-gray-900">
                        Fondations SEO technique
                      </td>
                      <td className="p-4 text-gray-700">
                        <strong>18 erreurs critiques</strong> : 0 headings HTML, meta description 1427 caractères, 
                        images non optimisées. Impact immédiat si corrigé.
                      </td>
                      <td className="p-4 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
                          <span className="text-lg font-bold text-red-700">3.5</span>
                        </div>
                        <p className="text-xs text-red-600 mt-1">/10</p>
                      </td>
                    </tr>
                    <tr className="border-b bg-red-50">
                      <td className="p-4 font-semibold text-gray-900">
                        Visibilité IA
                      </td>
                      <td className="p-4 text-gray-700">
                        <strong>Absent des recommandations</strong> ChatGPT, Perplexity, Gemini. 0 données structurées. 
                        40% du trafic futur provient des IA.
                      </td>
                      <td className="p-4 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
                          <span className="text-lg font-bold text-red-700">1.0</span>
                        </div>
                        <p className="text-xs text-red-600 mt-1">/10</p>
                      </td>
                    </tr>
                    <tr className="border-b bg-yellow-50">
                      <td className="p-4 font-semibold text-gray-900">
                        Stratégie de contenu
                      </td>
                      <td className="p-4 text-gray-700">
                        Site vitrine statique. <strong>0 article, 0 blog</strong>. Impossible de ranker sur 
                        requêtes informationnelles à fort volume.
                      </td>
                      <td className="p-4 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-100">
                          <span className="text-lg font-bold text-yellow-700">2.5</span>
                        </div>
                        <p className="text-xs text-yellow-600 mt-1">/10</p>
                      </td>
                    </tr>
                    <tr className="border-b bg-yellow-50">
                      <td className="p-4 font-semibold text-gray-900">
                        E-réputation & Avis
                      </td>
                      <td className="p-4 text-gray-700">
                        Base solide (818 avis 4.5★) mais croissance lente. Taux de réponse 75% insuffisant. 
                        Objectif : 1000+ avis.
                      </td>
                      <td className="p-4 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-100">
                          <span className="text-lg font-bold text-yellow-700">5.5</span>
                        </div>
                        <p className="text-xs text-yellow-600 mt-1">/10</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Constats clés */}
            <div className="mb-8 print:break-inside-avoid">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                🎯 Constats clés
              </h3>
              <div className="space-y-3 print:break-inside-avoid">
                <div className="flex items-start gap-3 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-800 leading-relaxed">
                    <strong className="text-red-900">Opportunité majeure non exploitée :</strong> Seulement 
                    7% du trafic provient de l'international alors que Paris accueille 15M de touristes/an. 
                    Perte estimée : <strong>150-250k€ CA/an</strong>.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-800 leading-relaxed">
                    <strong className="text-yellow-900">Fondations techniques défaillantes :</strong> 18 erreurs 
                    SEO critiques pénalisent le référencement. Score 52/100. Corrections = gain immédiat 
                    de <strong>15-25 positions Google</strong>.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-800 leading-relaxed">
                    <strong className="text-blue-900">Atouts à valoriser :</strong> Réputation solide (4.6★), 
                    identité gastronomique claire, atout "rooftop vue Tour Eiffel" (7300 recherches/mois) 
                    sous-exploité.
                  </p>
                </div>
              </div>
            </div>

            {/* Impact financier estimé */}
            <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl mb-8 print:break-inside-avoid">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-green-600" />
                Impact financier estimé
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-700 mb-2">Perte actuelle estimée</p>
                  <p className="text-3xl font-bold text-red-600">150-250k€</p>
                  <p className="text-xs text-gray-600">par an (opportunités manquées)</p>
                </div>
                <div>
                  <p className="text-sm text-gray-700 mb-2">Gain potentiel 6 mois</p>
                  <p className="text-3xl font-bold text-green-600">+40%</p>
                  <p className="text-xs text-gray-600">de réservations en ligne</p>
                </div>
              </div>
            </div>

            {/* Recommandation */}
            <div className="p-8 bg-gradient-to-br from-stratos-gold/10 to-yellow-50 rounded-2xl border-2 border-stratos-gold print:break-inside-avoid">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Star className="w-8 h-8 text-stratos-gold" />
                Recommandation STRATOS
              </h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Le <strong>Pack Premium STRATOS Hospitality</strong> permettra de :
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Corriger les <strong>18 erreurs SEO critiques</strong> et améliorer le score de 52/100 à 85+/100</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Capter la <strong>clientèle internationale</strong> (EN/ES/PT) avec hreflang et contenus traduits</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Optimiser le <strong>Google Business Profile</strong> pour atteindre 100% complétude et +150% visibilité</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Être <strong>recommandé par les IA</strong> (ChatGPT, Gemini) avec données structurées</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Instaurer un <strong>pilotage mensuel</strong> avec reporting KPIs et recommandations actionnables</span>
                </li>
              </ul>
              
              <div className="mt-6 p-4 bg-white rounded-lg border border-stratos-gold">
                <p className="text-center text-sm text-gray-700">
                  <strong className="text-stratos-gold">ROI estimé :</strong> 300-500% sur 12 mois • 
                  <strong className="text-stratos-gold ml-2">Délai premiers résultats :</strong> 90 jours
                </p>
              </div>
            </div>

            <p className="text-xs text-center text-gray-500 italic mt-8">
              Extrait confidentiel – STRATOS Hospitality – transmis exclusivement sous NDA signé
            </p>
          </div>
        </div>

        {/* Page 3: Introduction Pack Premium */}
        <div className="p-12 print:break-after-page">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              Le Pack Premium STRATOS
            </h2>
            
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-6 mb-12">
              <p className="text-xl text-gray-600 font-light leading-relaxed">
                Votre établissement mérite une visibilité exceptionnelle. Le Pack Premium STRATOS 
                combine expertise SEO technique, stratégie internationale et accompagnement personnalisé 
                pour propulser votre présence en ligne.
              </p>

              <div className="grid grid-cols-3 gap-6 my-12">
                <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                  <p className="text-4xl font-bold text-blue-900 mb-2">2000€</p>
                  <p className="text-sm text-blue-700 font-medium">par mois HT</p>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                  <p className="text-4xl font-bold text-green-900 mb-2">3</p>
                  <p className="text-sm text-green-700 font-medium">langues couvertes</p>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                  <p className="text-4xl font-bold text-purple-900 mb-2">300%</p>
                  <p className="text-sm text-purple-700 font-medium">ROI estimé en 6 mois</p>
                </div>
              </div>

              <div className="p-8 bg-gradient-to-br from-stratos-gold/10 to-yellow-50 rounded-2xl border-2 border-stratos-gold print:break-inside-avoid">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <Star className="w-8 h-8 text-stratos-gold" />
                  Pourquoi choisir le Pack Premium ?
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span><strong>Accompagnement rapproché :</strong> Réunions bi-mensuelles pendant 2 mois 
                    pour un démarrage optimal, puis mensuelles pour un suivi continu.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span><strong>Vision internationale :</strong> Développez votre visibilité sur 3 langues 
                    et touchez une clientèle internationale qualifiée.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span><strong>Expertise dédiée :</strong> 2h de consulting mensuel avec nos experts pour 
                    adapter la stratégie à vos objectifs business.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span><strong>Résultats mesurables :</strong> Dashboard STRATOS en temps réel et reporting 
                    mensuel détaillé pour piloter votre visibilité.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Page 3: Prestations détaillées */}
        <div className="p-12 print:break-after-page">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-2">
              Prestations Incluses
            </h2>
            <p className="text-gray-600 mb-12">
              Un accompagnement complet pour maximiser votre visibilité en ligne
            </p>

            <div className="space-y-6">
              {premiumFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-stratos-gold hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-stratos-gold to-yellow-600 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-gray-700 mb-2">
                          {feature.description}
                        </p>
                        <p className="text-sm text-gray-600 italic">
                          → {feature.details}
                        </p>
                      </div>
                      <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Page 4: Planning */}
        <div className="p-12 print:break-after-page">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-2">
              Planning des Livrables
            </h2>
            <p className="text-gray-600 mb-12">
              Roadmap des 3 premiers mois de collaboration
            </p>

            <div className="space-y-4 mb-12">
              {deliverables.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-5 bg-gradient-to-r from-gray-50 to-white border-l-4 border-stratos-gold rounded-lg"
                >
                  <div className="w-32 flex-shrink-0">
                    <Badge variant="outline" className="font-semibold">
                      {item.week}
                    </Badge>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium">{item.item}</p>
                  </div>
                  <Check className="w-5 h-5 text-stratos-gold" />
                </div>
              ))}
            </div>

            <div className="p-8 bg-blue-50 border-2 border-blue-200 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Calendar className="w-7 h-7 text-blue-600" />
                Rythme des Réunions Stratégiques
              </h3>
              <div className="space-y-4 text-gray-700">
                <div className="flex items-start gap-3">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-bold text-green-700">2x</span>
                  </div>
                  <div className="flex-1 pt-2">
                    <p className="font-semibold text-gray-900 mb-1">
                      Mois 1 & 2 : Réunions bi-mensuelles
                    </p>
                    <p className="text-sm text-gray-600">
                      2 réunions par mois pour un accompagnement rapproché durant la phase de lancement. 
                      Suivi des quick wins, validation des optimisations, ajustements stratégiques.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-bold text-blue-700">1x</span>
                  </div>
                  <div className="flex-1 pt-2">
                    <p className="font-semibold text-gray-900 mb-1">
                      À partir du mois 3 : Réunions mensuelles
                    </p>
                    <p className="text-sm text-gray-600">
                      1 réunion par mois pour le suivi continu, analyse des KPIs, présentation du reporting, 
                      et définition des actions du mois suivant.
                    </p>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-white rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Format :</strong> Visioconférence de 60-90 minutes • 
                    <strong className="ml-2">Participants :</strong> Votre équipe + Expert STRATOS • 
                    <strong className="ml-2">Support :</strong> Compte-rendu envoyé sous 48h
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page 5: Accès requis */}
        <div className="p-12 print:break-after-page">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-2">
              Accès Nécessaires
            </h2>
            <p className="text-gray-600 mb-12">
              Pour garantir l'efficacité de nos prestations, nous aurons besoin des accès suivants
            </p>

            {/* Essential Access */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Star className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Accès Essentiels
                </h3>
                <Badge variant="danger">Requis</Badge>
              </div>

              <div className="space-y-4">
                {requiredAccess.filter(a => a.priority === 'essential').map((access, index) => {
                  const Icon = access.icon;
                  return (
                    <div
                      key={index}
                      className="p-5 border-2 border-red-200 bg-red-50/50 rounded-lg"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-gray-900 mb-2">
                            {access.service}
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Comment :</strong> {access.instructions}
                          </p>
                          <p className="text-sm text-gray-600 italic">
                            <strong>Pourquoi :</strong> {access.why}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recommended Access */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Check className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Accès Recommandés
                </h3>
                <Badge variant="default">Pour optimiser</Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {requiredAccess.filter(a => a.priority === 'recommended').map((access, index) => {
                  const Icon = access.icon;
                  return (
                    <div
                      key={index}
                      className="p-4 border border-gray-200 bg-white rounded-lg hover:border-stratos-gold transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <Icon className="w-5 h-5 text-gray-400 mt-1" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {access.service}
                          </h4>
                          <p className="text-xs text-gray-600">
                            {access.why}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Security Note */}
            <div className="p-6 bg-gray-900 text-white rounded-xl">
              <div className="flex items-start gap-4">
                <Key className="w-8 h-8 text-stratos-gold flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">
                    🔒 Sécurité & Confidentialité
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Tous les accès sont traités de manière strictement confidentielle. Nous respectons 
                    le RGPD et nos engagements contractuels de confidentialité. Les identifiants sont 
                    stockés de manière sécurisée et utilisés uniquement dans le cadre de votre prestation SEO/GEO.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Last Page: Contact */}
        <div className="p-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Prêt à propulser votre visibilité ?
              </h2>
              <p className="text-xl text-gray-600">
                Commençons ensemble cette aventure digitale
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="p-8 bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Vos Contacts STRATOS
                </h3>
                <div className="space-y-4 text-gray-700">
                  <div>
                    <p className="font-semibold text-gray-900">Adrien Cerdan</p>
                    <p className="text-sm text-gray-600">Directeur adjoint</p>
                    <p className="text-sm text-blue-600">adrien@stratos360.fr</p>
                  </div>

                </div>
              </div>

              <div className="p-8 bg-gradient-to-br from-stratos-gold/10 to-yellow-50 border-2 border-stratos-gold rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Prochaines Étapes
                </h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-0.5">1</Badge>
                    <span className="text-sm">Signature du contrat & CGV</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-0.5">2</Badge>
                    <span className="text-sm">Transmission des accès requis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-0.5">3</Badge>
                    <span className="text-sm">Réunion kick-off (sous 7 jours)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-0.5">4</Badge>
                    <span className="text-sm">Démarrage des prestations</span>
                  </li>
                </ol>
              </div>
            </div>

            <div className="text-center pt-8 border-t-2 border-stratos-gold">
              <h1 
                className="text-5xl font-bold mb-3"
                style={{
                  background: 'linear-gradient(to right, #E9BC66, #F4D89E)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                STRATOS
              </h1>
              <p className="text-xl text-gray-600 mb-2">360</p>
              <div className="mt-6 text-xs text-gray-500">
                <p>SARL au capital de 150 500€ • SIRET : 822 096 913 00022</p>
                <p className="mt-1">TVA : FR82822096913</p>
                <p className="mt-2">adrien@stratos360.fr</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
};

